// React
import { gql, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

// Components
import { Card, Grid, Icon, Tooltip } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Data
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import dataInfracciones from "./data/dataInfracciones";
import ModalInfraccion from "./ModalInfraccion";

function Infracciones() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedInfraccion, setSelectedInfraccion] = useState(null);
  const [openModalInfraccion, setOpenModalInfraccion] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "ASC",
  });
  const { columns, getRows } = dataInfracciones;
  const [filter, setFilter] = useState({});

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getInfracciones($search: String, $page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        infracciones(
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          filter: $filter
        ) {
          id
          categoria
          fecha
          dni
          nombre
          apellido
          usuario {
            id
            nombre
            apellido
            dni
          }
          nroExpediente
          nroResolucion
          motivo
          monto
          localidad {
            nombre
          }
          sancion
          tipoInfraccion
          createdAt
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: {
          ...filter,
        },
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (data) {
      setRows(
        getRows(data.infracciones, (item) => {
          setSelectedInfraccion(item);
          setOpenModalInfraccion(true);
        })
      );
      setPagesInfo(data.paginationInfo);
    }
  }, [data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox
                  display={{
                    xs: "flex-row",
                    sm: "flex",
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftBox display="flex" alignItems="center">
                      <SoftBox mr={1} display="flex" alignItems="center">
                        <SoftBox
                          display="grid"
                          justifyContent="center"
                          alignItems="center"
                          bgColor="primary"
                          color="white"
                          width="3rem"
                          height="3rem"
                          shadow="md"
                          borderRadius="lg"
                          variant="gradient"
                        >
                          <Icon fontSize="default">report_gmailerrorred</Icon>
                        </SoftBox>
                      </SoftBox>
                      <SoftBox
                        display="flex-row"
                        alignItems="center"
                        sx={{
                          lineHeight: 0,
                        }}
                      >
                        <SoftTypography variant="h6" fontWeight="bold">
                          Infracciones
                        </SoftTypography>
                        <SoftTypography variant="caption" fontWeight="regular">
                          {pagesInfo?.total
                            ? `${pagesInfo?.total} infraccion${pagesInfo?.total > 1 ? "es" : ""}`
                            : "No hay infracciones"}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox
                      display={{
                        xs: "block",
                        sm: "none",
                      }}
                    >
                      <Tooltip title="Agregar nuevo usuario" placement="top">
                        <SoftButton
                          color="primary"
                          iconOnly
                          onClick={() => setOpenModalInfraccion(true)}
                        >
                          <Icon>add</Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    mt={{
                      xs: 2,
                      sm: 0,
                    }}
                    gap={2}
                  >
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder="Escribe aquí..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>
                    <SoftBox
                      display={{
                        xs: "none",
                        sm: "block",
                      }}
                    >
                      <Tooltip title="Agregar nueva infracción" placement="top">
                        <SoftButton color="primary" onClick={() => setOpenModalInfraccion(true)}>
                          <Icon>add</Icon>
                          &nbsp;Agregar
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : rows?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.infracciones.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          <ModalInfraccion
            open={openModalInfraccion}
            handleClose={() => {
              setOpenModalInfraccion(false);
              setSelectedInfraccion(null);
            }}
            idInfraccion={selectedInfraccion?.id}
            refetch={refetch}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Infracciones;
